import { computed, ref } from 'vue'
import { createGlobalState, useSwipe } from '@vueuse/core'
import { useHeader } from '@/composables/useHeader'
import { useDeviceWidth } from '@/composables/useDeviceWidth'
import { widthMapping } from '@/constants/mappings/width.mapping'

// 滑動門檻，比 useSwipe 預設 50 大一點，避免手機上正常操作（例如點擊、輕微拖曳）誤觸
const SWIPE_THRESHOLD_PX = 96
// 收尾滑出動畫時長（ms），需與 App.vue 的 .page-slide-back transition duration 一致
const COMPLETE_TRANSITION_MS = 250

/** 手機版向右滑動返回上一頁手勢：讓 App.vue（頁面平移）與 SwipeBackIndicator（返回圓鈕）共用同一份偵測結果 */
export const useSwipeBackGesture = createGlobalState(() => {
  const { showGoBack, goPrevPage } = useHeader()
  const { width } = useDeviceWidth()
  const isMobile = computed(() => width.value <= widthMapping['md'])

  // 放開手指且已達門檻：頁面正在滑出去、尚未真正換頁的過渡期
  const isCompleting = ref(false)

  const { isSwiping, direction, lengthX } = useSwipe(window, {
    threshold: SWIPE_THRESHOLD_PX,
    onSwipeEnd: (_e, endDirection) => {
      if (endDirection === 'right' && isMobile.value && showGoBack.value) {
        isCompleting.value = true
        setTimeout(() => {
          goPrevPage()
          isCompleting.value = false
        }, COMPLETE_TRANSITION_MS)
      }
    }
  })

  const isDraggingRight = computed(
    () => isMobile.value && showGoBack.value && isSwiping.value && direction.value === 'right'
  )

  // 返回圓鈕顯示條件：拖曳中，或已放開但還在滑出動畫期間
  const isActive = computed(() => isDraggingRight.value || isCompleting.value)

  // 頁面要平移的正值距離；lengthX = coordsStart.x - coordsEnd.x，向右滑動時是負值，取負號還原
  const dragX = computed(() => {
    if (isCompleting.value) return window.innerWidth
    if (!isDraggingRight.value) return 0
    return Math.min(Math.max(-lengthX.value, 0), window.innerWidth)
  })

  // 拖曳中要即時跟手（不能有 transition），放開瞬間才切換成有 transition 讓它彈回或滑出
  const isDragging = computed(() => isDraggingRight.value)

  return { isActive, dragX, isDragging }
})
